import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  useTheme,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import FilterPanel from 'components/DashboardComponents/FilterPanel';
import useApi from 'hooks/useApi';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { SearchParams } from 'api/ResourceAPI';
import { SelectedFiltersList } from 'types/models/SelectedFiltersList';
import KpiMetricsWorkflow from './KpiMetricsTabPages/KpiMetricsWorkflow';
import KpiMetricsAcceptance from './KpiMetricsTabPages/KpiMetricsAcceptance';
import KpiMetricsFinancial from './KpiMetricsTabPages/KpiMetricsFinancial';
import DashboardTabServices from './hooks/DashboardTabServices';
import Loader from 'components/Loader';
import transformPendingData from './helpers/transformPendingData';
import aggregateDonutData from './helpers/aggregateDonutData';

// --------- SAME DATA AS YOUR CODE (unchanged) ----------

type Selected = { [key: string]: string | string[] };
type FormattedSelected = { [key: string]: string };

// Helper function to initialize graph data based on metric type
const getInitialGraphData = (metricId?: number) => {
  if (metricId === 2) {
    return {
      orders: {
        client_acceptance: [],
        acceptance_data: [],
      },
    };
  }
  // Default for metric 1 and others
  return {
    orders: {
      consultant_submission: [],
      dm_validation: [],
      client_acceptance: [],
    },
  };
};

const KpiMetricsTab = () => {
  const theme = useTheme();
  const { makeCall } = useApi();
  const [t] = useTranslation();
  const { dispatch } = useAppState();
  type Metric = { id: number; page_title: string };
  const [metric, setMetric] = React.useState<Metric | null>(null);
  const { loadings } = useSelector((state) => state);
  const { filter, dashboard } = useSelector((state) => state);
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [showFilterPanel, setShowFilterPanel] = React.useState(true);

  const [metricsList, setMetricsList] = React.useState<Metric[]>([]);

  type Order = { mission_advancement_id: number; count: number };
  type GraphData = {
    orders: {
      consultant_submission: Order[];
      dm_validation: Order[];
      client_acceptance: Order[];
    };
    Message?: string;
  };

  const [graphData, setGraphData] = React.useState<GraphData>(() => getInitialGraphData());
  const [barChartData, setBarChartData] = React.useState<any[]>([]);
  const [gridData, setGridData] = React.useState<any[]>([]);

  // Reset graph data when metric changes
  useEffect(() => {
    if (metric?.id) {
      setGraphData(getInitialGraphData(metric.id));
    }
  }, [metric?.id]);

  const { fetchKpiMetricGraph, getKpiMetricSuperFilter, fetchKpiMetricGrid, downloadKpiGridExcel } =
    DashboardTabServices();

  useEffect(() => {
    const fetchKpiFilter = async () => {
      try {
        dispatch(addLoadingAction('dashboardSuperFilter'));
        const response = await getKpiMetricSuperFilter();
        if (response && response.metrics) {
          setMetricsList(response.metrics);
          if (response.metrics[0]) {
            setMetric(response.metrics[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch Super filter:', error);
      } finally {
        dispatch(removeLoadingAction('dashboardSuperFilter'));
      }
    };
    fetchKpiFilter();
  }, []);

  async function formatSelected(selected: Selected): Promise<SelectedFiltersList> {
    const output: SelectedFiltersList = {};

    const formatValue = (value: string | string[]): string | null => {
      if (Array.isArray(value)) {
        const cleanArray = value.filter((v) => v !== undefined && v !== null && v !== '');

        if (cleanArray.length === 0) return null;

        // FIX: Return null instead of 'all' so the key is removed from the output
        if (cleanArray.length === 1 && cleanArray[0] === 'ALL') return null;

        return cleanArray.sort().join(',');
      }

      // Optional: Handle cases where a single string "ALL" is passed
      if (value === 'ALL' || !value || value.trim() === '') return null;

      return value;
    };

    Object.entries(selected).forEach(([key, val]) => {
      if (!key || key === 'undefined' || key === 'null') return;
      if (val === undefined || val === null) return;

      const formatted = formatValue(val);

      // This condition now handles the 'ALL' case automatically
      if (formatted !== null) {
        output[key] = formatted;
      }
    });

    return output;
  }

  useEffect(() => {
    setCurrentPage(dashboard?.currentPage ?? 0);
  }, [dashboard?.currentPage]);

  useEffect(() => {
    setPageSize(dashboard?.pageSize ?? 10);
  }, [dashboard?.pageSize]);

  const getKpiMetricGrid = useCallback(
    async (data_type: number, params: SelectedFiltersList) => {
      try {
        dispatch(addLoadingAction('dashboardKpiGrid'));
        const result = await fetchKpiMetricGrid(data_type, params);

        if (result) {
          setGridData(result);
          dispatch(removeLoadingAction('dashboardKpiGrid'));
        }
      } catch (error) {
        console.error('Failed to fetch dashboardKpiGrid:', error);
      } finally {
        // Stop loading
        dispatch(removeLoadingAction('dashboardKpiGrid'));
      }
    },
    [dispatch, fetchKpiMetricGrid]
  );

  const getKpiMetricGraph = useCallback(
    async (data_type: number, params: SelectedFiltersList) => {
      try {
        dispatch(addLoadingAction('dashboardKpiGraph'));
        const result = await fetchKpiMetricGraph(data_type, params);
        if (result) {
          setGraphData(result);
          if (data_type === 1) {
            setBarChartData(transformPendingData(result?.orders?.pending_data, data_type));
          } else if (data_type === 2) {
            setBarChartData(transformPendingData(result?.orders?.acceptance_data, data_type));
          } else if (data_type === 3) {
            setBarChartData(transformPendingData(result?.orders?.pending_data, data_type));
          }
          // const barData = data_type === 1 ? result?.orders?.pending_data : result?.orders?.acceptance_data;

          dispatch(removeLoadingAction('dashboardKpiGraph'));
        }
      } catch (error) {
        console.error('Failed to fetch dashboardKpiGraph:', error);
      } finally {
        // Stop loading
        dispatch(removeLoadingAction('dashboardKpiGraph'));
      }
    },
    [dispatch, fetchKpiMetricGraph]
  );

  useEffect(() => {
    const fetchDonutGrid = async () => {
      const params = await formatSelected(filter.selected);
      // derive sort params from dashboard state
      let sort_field: string | undefined;
      let sort_by: string | undefined;
      if (dashboard?.sortKpiMetricGridData && dashboard.sortKpiMetricGridData.length > 0) {
        const { field, sort } = dashboard.sortKpiMetricGridData[0];
        sort_field = field;
        sort_by = sort?.toUpperCase();
      }

      // Prefer dashboard-controlled pagination if present to avoid double-triggering
      const page = dashboard?.currentPage ?? currentPage;
      const size = dashboard?.pageSize ?? pageSize;

      const searchParams: any = sort_field
        ? {
            size,
            page,
            sort_field,
            sort_by,
            ...params,
          }
        : {
            size,
            page,
            ...params,
          };

      if (params?.start_date && params?.end_date) {
        await getKpiMetricGrid(metric?.id || 1, searchParams);
      }
    };

    if (metric !== null && window.location.pathname.includes('dashboard')) {
      fetchDonutGrid();
    }
  }, [
    metric,
    JSON.stringify(filter.selected),
    dashboard?.sortKpiMetricGridData,
    dashboard?.currentPage,
    dashboard?.pageSize,
  ]);

  useEffect(() => {
    const fetchDonutGraph = async () => {
      const params = await formatSelected(filter.selected);
      if (params?.start_date && params?.end_date) {
        await getKpiMetricGraph(metric?.id || 1, params);
        // await getKpiMetricGrid(metric?.id || 1, params);
      }
    };

    if (metric !== null) fetchDonutGraph();
  }, [metric, JSON.stringify(filter.selected)]);

  // --- Aggregate Donut Data using helper (metric-aware) ---
  const { donutDataConsultant, donutDataDM, donutDataClient } = React.useMemo(
    () => aggregateDonutData(graphData?.orders || {}, metric?.id || 1),
    [graphData, metric?.id]
  );

  const handleDownloadXlsx = useCallback(async () => {
    try {
      const params = await formatSelected(filter.selected);

      dispatch(addLoadingAction('dashboardKpiExportXlsx'));

      const { data, filename } = await downloadKpiGridExcel(metric?.id || 1, params);

      if (!data) throw new Error('No XLSX data received');

      const a = document.createElement('a');
      a.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data}`;
      a.download = `${filename}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('Download XLSX failed:', e);
    } finally {
      dispatch(removeLoadingAction('dashboardKpiExportXlsx'));
    }
  }, [filter.selected, metric?.id, dispatch, downloadKpiGridExcel]);

  // Now you can use donutDataConsultant, donutDataDM, and donutDataClient in your component render.

  const COLORS_CONSULTANT = ['#61D2A4', '#FF6040'];
  const COLORS_DM = ['#c0c0c0', '#FF6040', '#FFC107', '#61D2A4', '#3B82F6'];
  const COLORS_CLIENT = ['#c0c0c0', '#FF6040', '#FFC107', '#61D2A4', '#3B82F6'];
  const RATING_COLORS = ['#60C39B', '#3C83F0', '#FFD200', '#FF9100', '#808080', '#E600E6', '#C0C0C0'];

  const isReduxLoading = Boolean(loadings?.dashboardSuperFilter) || metric === null;
  const isLoading = Boolean(isReduxLoading);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '95vh' }}>
        <Loader />
      </div>
    );
  }

  return (
    <Grid container spacing={3} sx={{ flexGrow: 1, p: 3, pt: 0, height: 'calc(100vh - 64px)' }}>
      {/* ----------- TOP FILTER ROW (Matches Screenshot) ----------- */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel id="metrics-select-label">{t('Metrics')}</InputLabel>
            <Select
              labelId="metrics-select-label"
              id="metrics-select"
              value={metric ? metric.id : ''}
              label="Metrics"
              onChange={(e) => {
                const selected = metricsList.find((m) => m.id === e.target.value);
                if (selected) setMetric(selected);
              }}
              disabled={!metricsList || metricsList.length === 0}
            >
              {metricsList.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.page_title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#1A73E8',
              color: '#fff',
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              boxShadow: '0px 2px 6px rgba(0,0,0,0.15)',
              '&:hover': {
                backgroundColor: '#1669C1',
              },
            }}
            onClick={() => setShowFilterPanel((p) => !p)}
          >
            {/* <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloseIcon sx={{ fontSize: 14, color: 'white' }} />
            </Box> */}
            Filter
          </Button>
        </Box>
      </Grid>

      {/* ------------ MAIN CONTENT (LEFT SIDE) ----------- */}
      <Grid item xs={12} lg={showFilterPanel ? 9.2 : 12} sx={{ overflowY: 'auto' }}>
        {metric?.id === 1 && (
          <React.Suspense fallback={<div>Loading...</div>}>
            <KpiMetricsWorkflow
              donutDataConsultant={donutDataConsultant}
              donutDataDM={donutDataDM}
              donutDataClient={donutDataClient}
              COLORS_CONSULTANT={COLORS_CONSULTANT}
              COLORS_DM={COLORS_DM}
              COLORS_CLIENT={COLORS_CLIENT}
              barChartData={barChartData}
              metric={metric}
              data_table={gridData}
              onDownloadXlsx={handleDownloadXlsx}
              showDownload={Boolean((gridData as any)?.orders?.totalItems)}
            />
          </React.Suspense>
        )}
        {metric?.id === 2 && (
          <React.Suspense fallback={<div>Loading...</div>}>
            <KpiMetricsAcceptance
              donutDataClient={donutDataClient}
              COLORS_CONSULTANT={COLORS_CONSULTANT}
              COLORS_DM={COLORS_DM}
              COLORS_CLIENT={COLORS_CLIENT}
              barChartData={barChartData}
              metric={metric}
              data_table={gridData}
              onDownloadXlsx={handleDownloadXlsx}
              showDownload={Boolean((gridData as any)?.orders?.totalItems)}
            />
          </React.Suspense>
        )}
        {metric?.id === 3 && (
          <React.Suspense fallback={<div>Loading...</div>}>
            <KpiMetricsFinancial
              donutDataClient={donutDataClient}
              COLORS_CONSULTANT={COLORS_CONSULTANT}
              COLORS_DM={COLORS_DM}
              COLORS_CLIENT={RATING_COLORS}
              barChartData={barChartData}
              metric={metric}
              data_table={gridData}
            />
          </React.Suspense>
        )}
      </Grid>
      {showFilterPanel && (
        <Grid item xs={12} lg={2.5}>
          <FilterPanel />
        </Grid>
      )}
    </Grid>
  );
};

export default KpiMetricsTab;
